import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { topRightStack } from '../utils/toastStack';
import './systemHealthToast.css';

const POLL_MS = 15000;
const TOAST_MS = 20000;

function canSeeSystemAlerts(user) {
  return user && (user.role === 'super_admin' || user.role === 'admin');
}

function formatBytes(value) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes)) return '-';
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  return `${Math.round(bytes / 1048576)} MB`;
}

function formatValue(alert) {
  if (!alert) return '';
  if (alert.unit === 'bytes') return formatBytes(alert.value);
  if (alert.unit === '%') return `${alert.value}%`;
  if (alert.unit) return `${alert.value} ${alert.unit}`;
  return String(alert.value ?? '');
}

function getServiceAlerts(status) {
  if (!status) return [];
  const alerts = [];

  if (status.websocket && !status.websocket.ok) {
    alerts.push({
      key: 'service-websocket',
      severity: 'critical',
      title: 'WebSocket service down',
      message: 'Realtime admin/public updates may not reach connected clients.',
      value: status.websocket.status || 'down',
      unit: '',
    });
  }

  if (status.redis && !status.redis.ok) {
    alerts.push({
      key: 'service-redis',
      severity: 'critical',
      title: 'Redis unavailable',
      message: 'Queued log processing and related background features may be degraded.',
      value: status.redis.status || 'down',
      unit: '',
    });
  }

  if (status.logReceiver && !status.logReceiver.ok) {
    alerts.push({
      key: 'service-log-receiver',
      severity: 'critical',
      title: 'Log receiver down',
      message: 'CS2 server logs may not be processed until the receiver is running again.',
      value: status.logReceiver.status || 'down',
      unit: '',
    });
  }

  return alerts;
}

const toneOf = (severity) => (severity === 'critical' ? '#ef4444' : '#f59e0b');

/** Alert icon: warning triangle (critical) or exclamation circle (warning). */
function AlertIcon({ critical }) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {critical
        ? <><path d="M12 3L2 20h20z" /><path d="M12 10v4M12 17h.01" /></>
        : <><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></>}
    </svg>
  );
}

export default function SystemHealthToast() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [toasts, setToasts] = useState([]);
  const shownRef = useRef(new Set());

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const pushToast = useCallback((alert) => {
    const dedupeKey = `${alert.key}:${alert.severity}`;
    if (shownRef.current.has(dedupeKey)) return;
    shownRef.current.add(dedupeKey);

    const id = `${dedupeKey}:${Date.now()}`;
    const toast = {
      ...alert,
      id,
      dedupeKey,
      ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };

    // Closes itself when its countdown bar ends (TOAST_MS); hovering pauses the bar.
    setToasts(prev => [toast, ...prev].slice(0, 4));
  }, []);

  const checkAlerts = useCallback(async () => {
    if (!canSeeSystemAlerts(user)) return;

    try {
      const [metricsRes, statusRes] = await Promise.allSettled([
        axios.get('/api/metrics'),
        axios.get('/api/status'),
      ]);
      const metricAlerts = metricsRes.status === 'fulfilled' && Array.isArray(metricsRes.value.data?.alerts)
        ? metricsRes.value.data.alerts
        : [];
      const serviceAlerts = statusRes.status === 'fulfilled' ? getServiceAlerts(statusRes.value.data) : [];
      const alerts = [...metricAlerts, ...serviceAlerts];
      const activeKeys = new Set(alerts.map(alert => `${alert.key}:${alert.severity}`));

      for (const key of Array.from(shownRef.current)) {
        if (!activeKeys.has(key)) shownRef.current.delete(key);
      }

      alerts.forEach(pushToast);
    } catch (_) {
      // Metrics requires super-admin access; failed checks should stay silent.
    }
  }, [pushToast, user]);

  useEffect(() => {
    if (!canSeeSystemAlerts(user)) {
      shownRef.current.clear();
      setToasts([]);
      return undefined;
    }

    checkAlerts();
    const interval = setInterval(checkAlerts, POLL_MS);
    return () => clearInterval(interval);
  }, [checkAlerts, user]);

  if (!toasts.length) return null;

  return createPortal(
    <>
      {toasts.map((toast) => {
        const critical = toast.severity === 'critical';
        const value = formatValue(toast);
        return (
          <div key={toast.id} className="sh-toast" style={{ '--sh-tone': toneOf(toast.severity) }}
            title={`${critical ? t('systemHealth.critical', 'System critical') : t('systemHealth.warning', 'System warning')}: ${toast.message}`}>
            <span className={`sh-ic${critical ? ' pulse' : ''}`}><AlertIcon critical={critical} /></span>
            <div className="sh-main">
              <div className="sh-top">
                <span className="sh-title">{toast.title}</span>
                {value && <span className="sh-value">{value}</span>}
                <span className="sh-time">{toast.ts}</span>
              </div>
              <div className="sh-msg">{toast.message}</div>
            </div>
            <button type="button" className="sh-x" aria-label={t('common.close', 'Close')} onClick={() => dismiss(toast.id)}>×</button>
            <span className="sh-bar" style={{ animationDuration: `${TOAST_MS}ms` }} onAnimationEnd={() => dismiss(toast.id)} />
          </div>
        );
      })}
    </>,
    topRightStack()
  );
}
